import { useMemo } from "react"
import type { SetURLSearchParams } from "react-router"

/** Single-value filter mirrored to a `?key=` URL param. */
export function stringFilterParam(
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  key: string,
): [string | null, (value: string | null) => void] {
  const value = searchParams.get(key)
  const setValue = (next: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (next) params.set(key, next)
      else params.delete(key)
      return params
    })
  }
  return [value, setValue]
}

/** Multi-value, comma-joined filter mirrored to a `?key=` URL param. */
export function useArrayFilterParam(
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  key: string,
): [string[], (values: string[]) => void] {
  const value = useMemo(
    () => searchParams.get(key)?.split(",").filter(Boolean) ?? [],
    [searchParams, key],
  )
  const setValue = (values: string[]) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (values.length > 0) params.set(key, values.join(","))
      else params.delete(key)
      return params
    })
  }
  return [value, setValue]
}

/** Removes every listed param in a single update (a `forEach` of individual
 *  `onSelectionChange` calls would each fire against the same pre-click
 *  `searchParams` snapshot, so only the last one would survive). */
export function clearUrlParams(
  setSearchParams: SetURLSearchParams,
  keys: string[],
) {
  setSearchParams((prev) => {
    const params = new URLSearchParams(prev)
    keys.forEach((key) => params.delete(key))
    return params
  })
}
